import { useNavigate } from 'react-router-dom';
import * as Page from '../../../components/content/page-header';
import { Content } from '../../../components/layout/content';
import * as Button from '../../../components/button/button';
import { Icons } from '../../../components/icons/icons';
import { PaginatedTable } from '../../../components/table/paginated-table';
import usePageState from '../../../hooks/use-page-state';
import { Modes } from '../../../types/page-modes';
import { Actions, Dialog, Title, Content as DialogContent } from '../../../components/dialog/dialog';
import { Label, TextInputLarge } from '../../../components/forms/input';
import { FieldValues, useForm } from 'react-hook-form';
import { FileInputLarge } from '../../../components/forms/file';
import { useState } from 'react';
import { Pageable, Query, usePagination } from '../../../hooks/use-pagination';
import { useDeleteMutation } from '../../../hooks/use-mutations';
import { HazardType } from './types';
import { ActionColumn } from '../../../components/table/columns';
import { usePostFormDataMutation } from '../../../hooks/use-form-mutations';

export const HazardTypes = (): JSX.Element => {
  const navigate = useNavigate();
  const [pageState, setPageState] = usePageState();
  const { register, handleSubmit, reset, formState: { errors } } = useForm();
  const [query, setQuery] = useState<Query>({});
  const hazardTypes = usePagination<Pageable<HazardType>>('/hazard-types', query);
  const postMutation = usePostFormDataMutation({ path: '/hazard-types' });
  const deleteMutation = useDeleteMutation();

  const onSubmit = (data: FieldValues): void => {
    postMutation.mutate(data);
    setPageState(Modes.BROWSE);
  };

  const addHazardSeverity = (): void => {
    reset();
    setPageState(Modes.CREATE);
  };

  const onDeleteConfirm = (): void => {
    deleteMutation.mutate({ path: `/hazard-types/${pageState.item?.id as string}` });
    setPageState(Modes.BROWSE);
  };

  const onUpdate = (item: HazardType): void => {
    navigate(`/settings/hazard-types/${item.id}`);
  };

  const columns = [
    { accessor: 'iconUrl', Header: 'Icon', Cell: (item: any): JSX.Element => {
      return item.row.original.iconUrl ? <img src={item.row.original.iconUrl} width={29} height={29} /> : <span />;
    } },
    { accessor: 'slug', Header: 'Id' },
    { accessor: 'name', Header: 'Name' },
    { accessor: 'associatedLayers', Header: 'Associated Layers' },
    ActionColumn({ onDelete: (item: HazardType): void => setPageState(Modes.DELETE, item), onUpdate })
  ];

  return <>
    <Content>
      <Page.Header>
        <Page.Title>Hazard Types</Page.Title>
        <Page.SubTitle>
          Hazard Types in RAPIDS.
          <div>
            <Button.LightBlackSmall onClick={addHazardSeverity}>
              <Icons.Add />
              Add Hazard Type
            </Button.LightBlackSmall>
          </div>
        </Page.SubTitle>
      </Page.Header>
      <PaginatedTable isLoading={hazardTypes.isLoading} columns={columns} data={hazardTypes.data} onChange={setQuery} />
    </Content>
    {pageState.mode === Modes.CREATE && (
      <Dialog onCancel={(): void => setPageState(Modes.BROWSE)} backdropClose={false}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Title>Add Hazard Type</Title>
          <DialogContent>
            <div className="mb-4">
              <Label name="slug">Hazard Type Id</Label>
              <TextInputLarge {...register('slug', { required: 'You must provide a value for Hazard Type Id.' })} errors={errors} placeholder="Hazard Type Id" />
            </div>
            <div className="mb-4">
              <Label name="name">Hazard Type Name</Label>
              <TextInputLarge {...register('name', { required: 'You must provide a value for Hazard Type Name.' })} errors={errors} placeholder="Hazard Type Name" />
            </div>
            <div className="mb-4">
              <Label>Hazard Type Icon</Label>
              <FileInputLarge { ...register('uploadIcon', { required: 'You must provide a value for Hazard Type Icon.' })} errors={errors} accept="image/png" />
              <span className="supported-file-format">Supported image format: PNG</span>
            </div>
          </DialogContent>
          <Actions>
            <Button.Tertiary onClick={(): void => setPageState(Modes.BROWSE)}>Cancel</Button.Tertiary>
            <Button.Primary type="submit">
              <Icons.Add /> Add
            </Button.Primary>
          </Actions>
        </form>
      </Dialog>
    )}
    {pageState.mode === Modes.DELETE && (
      <Dialog onCancel={(): void => setPageState(Modes.BROWSE)} backdropClose={false}>
        <Title>Delete Hazard Type</Title>
        <DialogContent>Are you sure you want to delete?</DialogContent>
        <Actions>
          <Button.Tertiary onClick={(): void => setPageState(Modes.BROWSE)}>Cancel</Button.Tertiary>
          <Button.Danger onClick={onDeleteConfirm}>Delete</Button.Danger>
        </Actions>
      </Dialog>
    )}
  </>;
};
